/* eslint-disable @typescript-eslint/no-explicit-any */
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { getAsset, listAssets, listUnlinkedAssets, patchAsset } from '../Api/Asset/AssetApi';
import { addError } from './Error';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';
import { DrawerData } from './Drawer';
import { ValidatorResult } from '../components/Compounds/CardWithTitle';
import { isData, isObserver } from './User';

export interface Asset {
  number: string | null;
  id: string;
  sf_id: string;
  upstackId: string;
  accountGri: string;
  accountId: string | null;
  name: string;
  supplierId: string;
  supplierSFID: string;
  supplierName: string;
  customer: string;
  tsd: string;
  salesAgent: string | null;
  directSales: string | null;
  latestSalesAssignmentDate: string | null;
  commissionsExperienceAnalyst: string | null;
  customerSuccessManager: string | null;
  assetStatus: string;
  commissionsStatus: string | null;
  actualDecommissionedDate: string | null;
  billingCommencementDate: string | null;
  contractSignDate: string | null;
  disconnectDate: string | null;
  disconnectedReason: string | null;
  expectedDisconnectDate: string | null;
  expectedFirstCommissionRun: string | null;
  actualFirstCommissionRun: string | null;
  actualFirstSpiffRun: string | null;
  commissionableMRC: number | null;
  grossMRR: number | null;
  totalMRC: number | null;
  netNRR: number | null;
  netMRR: number | null;
  commissionableNRC: number | null;
  nonCommissionableNRC: number | null;
  recurringCommissionPercent: number | null;
  nonRecurringCommissionPercent: number | null;
  grossNRR: number | null;
  installDate: string | null;
  activationDate: string | null;
  upcomingRenewalDate: string | null;
  cancellationDate: string | null;
  estimatedDeliveryDate: string | null;
  cancelledDate: string | null;
  contract: string | null;
  locationName: string | null;
  locationSFID: string | null;
  locationAddress: string | null;
  salesForceId: string | null;
  expectedDecommissionDate: string | null;
  supplierPaymentFrequency: string | null;
  paymentFrequencyOverride: string | null;
  supplierPaymentFrequencyException: string | null;
  paymentFrequency: string | null;
  expectedRpmAccountNumber: string | null;
  complimentaryBillingMonths: string | null;
  accountNumber: string | null;
  supplierOrderNumber: string | null;
  tsdTrackingNumber: string | null;
  linkId: string | null;
  circuitId: string | null;
  contractNumber: number | null;
  contractId: string | null;
  commissionsValidatedDate: string | null;
  commissionsValidationStatus: string | null;
  commissionsValidationSubStatus: string | null;
  legacyArchiveReason: string | null;
  decommissionReason: string | null;
  decommissionNotes: string | null;
  openCommissionsTicket: boolean;
  ticketCount: number | null;
  rpmAccountCount: number | null;
  parentCustomer: string | null;
  parentCustomerId: string | null;
  ultimateParent: string | null;
  ultimateParentId: string | null;
  createdAt: string | null;
  modifiedDate: string | null;
  split1SubAgent: string | null;
  split1Rate: number | null;
  split2SubAgent: string | null;
  split2Rate: number | null;
  x2Split: string | null;
  x2Rate: number | null;
  serviceContractSFID: string | null;
  opportunitySFID: string | null;
  quoteSFID: string | null;
  quoteRepInvolved: boolean | null;
  quoteTeamSellingApprovalNumber: string | null;
  quoteTeamSellingDiscount: number | null;
  projectSFID: string | null;
  projectNumber: string | null;
  tsdSFID: string | null;
  salesAgentSFID: string | null;
  quoteType: string | null;
  lineType: string | null;
  replacedAssetID: string | null;
  replacedAssetNumber: string | null;
  replacedAssetSFID: string | null;
  replacementOfAssetID: string | null;
  replacementOfAssetNumber: string | null;
  replacementOfAssetSFID: string | null;
  notesToCommissions: string | null;
  description: string | null;
  blendedRate: boolean | null;
  parentCommissionsStatus: string | null;
  firstRunBeforeSign: boolean;
  firstRunBeforeParentDecommission: boolean;
  netRevenue: number | null;
  directSalesPaid: string | null;
  directSalesPaidPercent: number | null;
  lastCommissionRun: string | null;
  expectedVariableComponents: string | null;
  dateAutoRenewed: string | null;
  cancelledReason: string | null;
}

export type AssetUpdate = Partial<Asset> & Pick<Asset, 'id'>;

export interface UnlinkedAsset {
  id: string;
  sf_id: string;
  assetNumber: string;
  commissionStatus: string;
  commissionsStatus?: string;
  status: string;
  supplierName: string;
  assetType: string;
}

// Asset subjects
const assetSubject = new BehaviorSubject<Asset>({} as Asset);
const assetDetailSubject = new BehaviorSubject<DrawerData | undefined>(undefined);
const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
const cruxAssetsSubject = new BehaviorSubject<PaginatedResponse<Asset>>({} as PaginatedResponse<Asset>);
const assetsByRpmAcctSub = new BehaviorSubject<PaginatedResponse<Asset>>({} as PaginatedResponse<Asset>);
const assetsByRpmAcctDrawerSub = new BehaviorSubject<PaginatedResponse<Asset>>({} as PaginatedResponse<Asset>);
const unlinkedAssetSub = new BehaviorSubject<UnlinkedAsset[]>([] as UnlinkedAsset[]);
const assetsByTicketIdSubject = new BehaviorSubject<PaginatedResponse<Asset>>({} as PaginatedResponse<Asset>);
const assetsByTicketIdDrawerSubject = new BehaviorSubject<PaginatedResponse<Asset>>({} as PaginatedResponse<Asset>);

// Loading Subjects
const assetLoadingSubject = new BehaviorSubject<boolean>(false);
const cruxAssetsLoadingSubject = new BehaviorSubject<boolean>(false);
const assetsByRpmAcctLoadingSubject = new BehaviorSubject<boolean>(false);
const assetsByRpmAcctLoadingDrawerSubject = new BehaviorSubject<boolean>(false);
const unlinkedAssetLoadingSubject = new BehaviorSubject<boolean>(false);
const updatingAssetSubject = new BehaviorSubject<boolean>(false);
const assetsByTicketIdLoadingSubject = new BehaviorSubject<boolean>(false);
const assetsByTicketIdLoadingDrawerSubject = new BehaviorSubject<boolean>(false);

const clearError = () => addError('asset', undefined);

export const getAssetSubject = (): BehaviorSubject<Asset> => assetSubject;
export const getAssetLoadingSubject = (): BehaviorSubject<boolean> => assetLoadingSubject;

export const getAssetDetailSubject = (): BehaviorSubject<DrawerData | undefined> => assetDetailSubject;

export const getCruxAssetsSubject = (): BehaviorSubject<PaginatedResponse<Asset>> => cruxAssetsSubject;
export const getCruxAssetsLoadingSubject = (): BehaviorSubject<boolean> => cruxAssetsLoadingSubject;

export const getAssetsByRpmAcctSub = (): BehaviorSubject<PaginatedResponse<Asset>> => assetsByRpmAcctSub;
export const getAssetsByRpmAcctDrawerSub = (): BehaviorSubject<PaginatedResponse<Asset>> => assetsByRpmAcctDrawerSub;
export const getAssetsByRpmAcctLoadingSubject = (): BehaviorSubject<boolean> => assetsByRpmAcctLoadingSubject;
export const getAssetsByRpmAcctLoadingDrawerSubject = (): BehaviorSubject<boolean> =>
  assetsByRpmAcctLoadingDrawerSubject;

export const getUnlinkedAssetSub = (): BehaviorSubject<UnlinkedAsset[]> => unlinkedAssetSub;
export const getUnlinkedAssetLoadingSubject = (): BehaviorSubject<boolean> => unlinkedAssetLoadingSubject;
export const getAssetsByTicketIdSubject = (): BehaviorSubject<PaginatedResponse<Asset>> => assetsByTicketIdSubject;
export const getAssetsByTicketIdDrawerSubject = (): BehaviorSubject<PaginatedResponse<Asset>> =>
  assetsByTicketIdDrawerSubject;
export const getAssetsByTicketIdLoadingSubject = (): BehaviorSubject<boolean> => assetsByTicketIdLoadingSubject;
export const getAssetsByTicketIdLoadingDrawerSubject = (): BehaviorSubject<boolean> =>
  assetsByTicketIdLoadingDrawerSubject;

export const updateAssetDetail = (assetData: DrawerData) => assetDetailSubject.next(assetData);

export const isAsset = (obj: any): obj is Asset => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    typeof (obj as any).id === 'string' &&
    typeof (obj as any).name === 'string' &&
    // optional properties can be checked if present:
    ('grossMRR' in obj && (obj as any).grossMRR ? typeof (obj as any).grossMRR === 'number' : true)
  );
};

export const getAssetById = async (id: string): Promise<Asset> => {
  const asset = cruxAssetsSubject?.getValue()?.data?.find((a) => a?.id === id);

  if (asset) {
    return asset;
  } else {
    const { data, error } = await getAsset(id);
    if (error) addError('asset', error.message);
    if (data) return data;
  }

  return {} as Asset;
};

export const fetchAsset = async (id: string, updateDrawer?: boolean): Promise<void | number> => {
  assetLoadingSubject.next(true);

  const { data, error } = await getAsset(id);

  if (data) {
    clearError();
    const detailSubject = getAssetDetailSubject().getValue() as Asset | undefined;
    if (updateDrawer || detailSubject?.id === data.id) updateAssetDetail(data);
    const subject = getAssetSubject().getValue();
    if (!updateDrawer || !subject.id || subject.id === data.id) assetSubject.next(data);
  }
  if (error) {
    addError('asset', error.message);
    const statusCode = error.errorResponse?.status || 500;
    return statusCode;
  }

  assetLoadingSubject.next(false);
};

export const fetchAssets = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
  inboundFetchNumberSubject.next(newFetchNumber);
  if (cruxAssetsSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  cruxAssetsLoadingSubject.next(true);

  const { data, error } = await listAssets(filters);
  const currentFetchNumber = inboundFetchNumberSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError();
    cruxAssetsSubject.next(data);
  }
  if (error) addError('asset', error.message);

  newFetchNumber === currentFetchNumber && cruxAssetsLoadingSubject.next(false);
};

export const fetchAssetsByRpmAcctId =
  (id: string, forDrawer = false) =>
  async (filters?: ApiAdvancedFilter) => {
    const loading = forDrawer ? assetsByRpmAcctLoadingDrawerSubject : assetsByRpmAcctLoadingSubject;
    const subject = forDrawer ? assetsByRpmAcctDrawerSub : assetsByRpmAcctSub;
    loading.next(true);

    const mergedFilters: ApiAdvancedFilter = {
      page: 1,
      pageSize: 50,
      ...filters,
      orders: filters?.orders ?? [{ sort: 'number', direction: 'desc' }],
      filters: [...(filters?.filters || []), { field: 'rpmAccountId', operator: 'equals', value: id }]
    };
    const { data, error } = await listAssets(mergedFilters);

    if (data) {
      clearError();
      subject.next(data);
    }
    if (error) addError('asset', error.message);

    loading.next(false);
  };

export const fetchUnlinkedAssets = async (id: string, supplierId?: string) => {
  unlinkedAssetLoadingSubject.next(true);

  const { data, error } = await listUnlinkedAssets(id, supplierId);

  if (data) {
    clearError();
    unlinkedAssetSub.next(data);
  }
  if (error) addError('asset', error.message);

  unlinkedAssetLoadingSubject.next(false);
};

export const updateAsset = async (asset: AssetUpdate, updateDrawer?: boolean) => {
  updatingAssetSubject.next(true);

  const { data, error } = await patchAsset(asset);

  updatingAssetSubject.next(false);

  if (error) addError('asset', error.message);
  if (data) {
    clearError();
    fetchAsset(asset.id, updateDrawer);
  }
  return { data, error };
};

export const fetchAssetsByTicketId =
  (id: string, forDrawer = false) =>
  async (filters?: ApiAdvancedFilter) => {
    const loading = forDrawer ? assetsByTicketIdLoadingDrawerSubject : assetsByTicketIdLoadingSubject;
    const subject = forDrawer ? assetsByTicketIdDrawerSubject : assetsByTicketIdSubject;
    loading.next(true);
    const mergedFilters: ApiAdvancedFilter = {
      page: 1,
      pageSize: 50,
      ...filters,
      orders: filters?.orders ?? [{ sort: 'number', direction: 'desc' }],
      filters: [...(filters?.filters || []), { field: 'ticketId', operator: 'equals', value: id }]
    };
    const { data, error } = await listAssets(mergedFilters);

    if (error) addError('asset', error.message);

    loading.next(false);

    if (data) {
      clearError();
      subject.next(data);
      return data;
    }
  };

export const handleCommissionsInfoValidation = (d: Asset | null): ValidatorResult => {
  const result: ValidatorResult = {
    isValid: true,
    messages: [],
    invalidFields: []
  };

  if (isObserver() || isData() || !d) return result;

  if (d.commissionsStatus === 'Decommissioned' && d.actualDecommissionedDate === null) {
    result.isValid = false;
    result.invalidFields.push('actualDecommissionedDate');
    result.messages.push({ field: 'actualDecommissionedDate', message: 'Actual Decommission Run is required.' });
  }

  return result;
};

export const handleRpmAccountValidation = (d: Asset | null): ValidatorResult => {
  const result: ValidatorResult = {
    isValid: true,
    messages: [],
    invalidFields: []
  };
  if (isObserver() || isData() || !d) return result;

  if (
    (d.commissionsStatus === 'Commissioning' || d.commissionsStatus === 'Partially Commissioning') &&
    d.rpmAccountCount == 0
  ) {
    result.isValid = false;
    result.invalidFields.push('rpmAccountCount');
    result.messages.push({ field: 'rpmAccountCount', message: 'Link to RPM Account is required.' });
  }

  return result;
};

export const handleValidationStatusValidation = (d: Asset | null): ValidatorResult => {
  const result: ValidatorResult = {
    isValid: true,
    messages: [],
    invalidFields: []
  };

  if (isObserver() || isData() || !d) return result;

  if (
    (d.commissionsStatus === 'Commissioning' || d.commissionsStatus === 'Partially Commissioning') &&
    d.commissionsValidationStatus === null
  ) {
    result.isValid = false;
    result.messages.push({ field: 'commissionsValidationStatus', message: 'Validation Status is required.' });
    result.invalidFields.push('commissionsValidationStatus');
  }

  return result;
};

export const isShowDecommissionModalToParentAsset = (asset: Asset, parentAsset: Asset | null): boolean => {
  if (isObserver() || isData() || !asset || !parentAsset) return false;
  return (
    asset.replacementOfAssetID === parentAsset.id &&
    asset.commissionsStatus === 'Commissioning' &&
    parentAsset.commissionsStatus === 'Commissioning' &&
    asset.actualFirstCommissionRun !== null
  );
};
